import { Attachment } from "@/api/attachment";
import { ImageFullscreen } from "@/components/modal/imageFullscreen";
import { SupportedLocale } from "@/i18n";
import {
  CATALOG_FICHE_MAIN_MEDIA_CLASS,
  CATALOG_FICHE_MAIN_MEDIA_WRAPPER_CLASS,
  CATALOG_THUMBNAIL_BOX_CLASS,
  CATALOG_THUMBNAIL_COLUMN_CLASS,
  CATALOG_THUMBNAIL_IMAGE_CLASS,
} from "@/pages/dashboard/shared/catalogMediaThumbnail";

interface ArtworkImagesProps {
  mainImage: Attachment | null;
  otherImages: Attachment[];
  locale: SupportedLocale;
  /**
   * Remplit sa colonne (aperçu CMS avancé, déjà dans une colonne à 50 %) au
   * lieu de se plafonner à la moitié de la fiche.
   */
  fillColumn?: boolean;
}

function getAttachmentLegend(
  attachment: Attachment,
  locale: SupportedLocale,
): string {
  const caption = attachment.caption?.[locale];
  const textAlt = attachment.textAlt?.[locale];
  return caption || textAlt || attachment.name || "";
}

export function ArtworkImages({
  mainImage,
  otherImages,
  locale,
  fillColumn = false,
}: ArtworkImagesProps) {
  const allImages = mainImage ? [mainImage, ...otherImages] : otherImages;
  if (allImages.length === 0) return null;

  const mainAttachment = allImages[0];
  const thumbnails = mainImage ? otherImages : otherImages.slice(1);
  const mainLegend = getAttachmentLegend(mainAttachment, locale);

  return (
    <div
      className={`relative flex w-full shrink-0 flex-col gap-4 text-left ${fillColumn ? "" : "md:max-w-[50%]"}`}
    >
      <div className={CATALOG_FICHE_MAIN_MEDIA_WRAPPER_CLASS}>
        <ImageFullscreen
          catalogFullscreen
          media={{
            attachments: [mainAttachment],
            type: "artwork",
            alt: mainLegend,
            width: 900,
            height: 900,
            className: CATALOG_FICHE_MAIN_MEDIA_CLASS,
          }}
        />
      </div>

      {thumbnails.length > 0 && (
        <div className="w-full flex flex-wrap gap-4">
          {thumbnails.map((attachment) => {
            const legend = getAttachmentLegend(attachment, locale);
            return (
              <div key={attachment.id} className={CATALOG_THUMBNAIL_COLUMN_CLASS}>
                <div className={CATALOG_THUMBNAIL_BOX_CLASS}>
                  <ImageFullscreen
                    catalogFullscreen
                    media={{
                      attachments: [attachment],
                      type: "artwork",
                      alt: legend || "",
                      width: 100,
                      height: 100,
                      className: CATALOG_THUMBNAIL_IMAGE_CLASS,
                    }}
                  />
                </div>
                {legend && (
                  <span className="text-xs text-muted-foreground line-clamp-2 text-center w-full px-1">
                    {legend}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
